import { newInternalLogger as internalLogger } from '../../../../services/loggerService/newInternalLogger';
import { _getLoggerOptions } from '../../../../services/loggerService/utils/LocalOptions';
import { decoratorUtil } from './decorators.util';

/**
 * D3 method decorator for logging method execution
 * Uses Proxy with Reflect.apply for enhanced "this" context handling
 * Works with both synchronous and asynchronous methods
 * @example
 * // Basic usage without options
 * @d3()
 * syncMethod() {
 *   // Sync method code
 * }
 *
 * @example
 * // With custom debug message
 * @d3({
 *   message: 'Custom debug message'
 * })
 * async asyncMethod() {
 *   // Async method code
 * }
 *
 * @param options Configuration options
 * @param options.message Optional message to include in the debug log
 * @returns Method decorator
 */

export function d3(options: { message?: string } = {}): any {
  return function (
    target: any,
    propertyKey: string | symbol,
    descriptor?: PropertyDescriptor
  ): void {
    // Handle case where descriptor might be undefined (for static methods)
    let needsDefineProperty = false;
    if (!descriptor) {
      descriptor = decoratorUtil.getOwnPropertyDescriptor(target, propertyKey);
      needsDefineProperty = true;
    }

    // Store the original method implementation
    const originalMethod = descriptor.value;
    const className = decoratorUtil.getClassName(target);
    const methodName = String(propertyKey);

    // Replace the original method with a Proxy that intercepts calls
    descriptor.value = new Proxy(originalMethod, {
      apply(target, thisArg, args) {
        // Execute the original method using Reflect.apply for proper "this" handling
        const result = Reflect.apply(target, thisArg, args);

        // Check if decorators are enabled
        const enableDecorators = _getLoggerOptions()?.enableDecorators;

        // If decorators are disabled or logger is not available, leave the original method unchanged
        if (!enableDecorators || !internalLogger?.debug?.d3) {
          return result;
        }

        /**
         * Runtime detection for async methods by identifying if it returns a Promise
         * This detection method was particularly chosen over validating the constructor.name
         * due to constructor.name's transformation in the environment
         */
        const isAsyncMethod =
          result && typeof (result as { then?: unknown }).then === 'function';

        if (!isAsyncMethod) {
          // For sync methods, log immediately
          internalLogger.debug.d3({
            className: className,
            methodName: methodName,
            message: options.message || `${className}.${methodName} completed`,
            isAsyncMethod: false,
            returnValue: result
          });
          return result;
        }

        // For async methods, attach logging to the promise chain
        return (result as Promise<unknown>).then((resolvedValue) => {
          // Runtime check for logger availability in async context too
          if (internalLogger?.debug?.d3) {
            internalLogger.debug.d3({
              className: className,
              methodName: methodName,
              message:
                options.message || `${className}.${methodName} completed`,
              isAsyncMethod: true,
              returnValue: resolvedValue
            });
          }
          return resolvedValue;
        });
      }
    });

    // Define property for fallback descriptor if needed
    if (needsDefineProperty) {
      Object.defineProperty(target, propertyKey, descriptor);
    }
  };
}
